import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { ExpenseDTO, ExpenseSummaryResponse, ExpenseView, Page, SaveExpenseRequest } from '@six-pockets/shared';
import { expensesApi } from '@/api';
import { PlusIcon, SearchIcon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button, ConfirmModal, InlineAlert, Pagination, PeriodPicker, Tabs, TextField, type TabOption } from '@/components/ui';
import { ExpenseFormModal } from '@/features/ledgers/components/ExpenseFormModal';
import { ExpenseRow } from '@/features/ledgers/components/ExpenseRow';
import { WithdrawModal } from '@/features/ledgers/components/WithdrawModal';
import { PEOPLE, SOURCE_LABEL } from '@/features/ledgers/sources';
import { selectionPhrase, thisMonthKey } from '@/features/reports/utils';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cx } from '@/utils/cx';
import { formatINR } from '@/utils/format';
import styles from '@/styles/pages/ExpenseLedgerPage/style.scss';

const PAGE_SIZE = 12;

const VIEW_OPTIONS: TabOption<ExpenseView>[] = [
  { value: 'expenses', label: 'Expenses' },
  { value: 'withdrawals', label: 'Withdrawals' },
];

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The month's figures above the list: what was spent and who paid (Expenses), or what was withdrawn (Withdrawals). */
function Summary({ view, summary: s, phrase }: { view: ExpenseView; summary: ExpenseSummaryResponse; phrase: string }) {
  if (view === 'withdrawals') {
    return (
      <section className={styles.summary} aria-label="Withdrawals summary">
        <div className={styles.lead}>
          <span className={styles.statLabel}>Withdrawn {phrase}</span>
          <span className={styles.leadValue}>{formatINR(s.withdrawn)}</span>
          <span className={styles.statNote}>{plural(s.withdrawalCount, 'withdrawal', 'withdrawals')}</span>
        </div>
        {PEOPLE.map((p) => (
          <div key={p} className={styles.stat}>
            <span className={styles.statLabel}>{SOURCE_LABEL[p]}</span>
            <span className={styles.statValue}>{formatINR(s.withdrawnBy[p])}</span>
          </div>
        ))}
      </section>
    );
  }

  // Waived customer balances (Pay now) are in the total but no one paid them, so they are noted on their own.
  const byPeople = s.total - s.bySource.counter - s.bySource.waiver;
  const who = PEOPLE.filter((p) => s.bySource[p] > 0).map((p) => `${SOURCE_LABEL[p]} ${formatINR(s.bySource[p])}`);
  return (
    <section className={styles.summary} aria-label="Expenses summary">
      <div className={styles.lead}>
        <span className={styles.statLabel}>Spent {phrase}</span>
        <span className={styles.leadValue}>{formatINR(s.total)}</span>
        <span className={styles.statNote}>
          {plural(s.count, 'expense', 'expenses')}
          {s.bySource.waiver > 0 && ` · ${formatINR(s.bySource.waiver)} waived balances`}
        </span>
      </div>
      <div className={styles.stat}>
        <span className={styles.statLabel}>Cash counter</span>
        <span className={styles.statValue}>{formatINR(s.bySource.counter)}</span>
      </div>
      <div className={styles.stat}>
        <span className={styles.statLabel}>Paid by others</span>
        <span className={styles.statValue}>{formatINR(byPeople)}</span>
        {who.length > 0 && <span className={styles.statNote}>{who.join(' · ')}</span>}
      </div>
      <div className={styles.stat}>
        <span className={styles.statLabel}>Still to withdraw</span>
        <span className={cx(styles.statValue, s.toWithdraw > 0 && styles.owed)}>{formatINR(s.toWithdraw)}</span>
        <span className={styles.statNote}>{plural(s.toWithdrawCount, 'expense', 'expenses')}, any month</span>
      </div>
    </section>
  );
}

/**
 * Expense ledger. Expenses: the month's expenses (this month by default) with a summary on top, Add expense, and
 * Withdraw on those paid by someone (staff, Sai, Bharath), which records that they took the amount back from the
 * business, out of the cash counter or the bank. Withdrawals: what was withdrawn in the month. Search (reason) and paging are done by the server; the tab
 * is in the URL (?view=withdrawals).
 */
export function ExpenseLedgerPage() {
  const [params, setParams] = useSearchParams();
  const view: ExpenseView = params.get('view') === 'withdrawals' ? 'withdrawals' : 'expenses';

  const [month, setMonth] = useState(thisMonthKey);
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 300);
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);

  const [result, setResult] = useState<Page<ExpenseDTO> | null>(null);
  const [summary, setSummary] = useState<ExpenseSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<ExpenseDTO | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ExpenseDTO | null>(null);
  const [withdrawing, setWithdrawing] = useState<ExpenseDTO | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // A new search goes back to the first page.
  useEffect(() => setPage(1), [query]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    expensesApi.list({ view, month, q: query || undefined, page, pageSize: PAGE_SIZE }, ctrl.signal).then(
      (r) => {
        // Deleting the last expense on a page leaves it empty: step back a page.
        if (r.items.length === 0 && r.page > 1) setPage(r.page - 1);
        else setResult(r);
        setLoading(false);
      },
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(messageOf(err));
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [view, month, query, page, reloadKey]);

  useEffect(() => {
    const ctrl = new AbortController();
    expensesApi.summary({ month }, ctrl.signal).then(setSummary, (err) => {
      if (!ctrl.signal.aborted) setError(messageOf(err));
    });
    return () => ctrl.abort();
  }, [month, reloadKey]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setModalError(null);
    try {
      await fn();
      setEditing(null);
      setDeleting(null);
      setWithdrawing(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setModalError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const save = (body: SaveExpenseRequest) => {
    const target = editing;
    if (!target) return;
    void run(() => (target === 'new' ? expensesApi.create(body) : expensesApi.update(target.id, body)));
  };

  const closeModal = () => {
    if (busy) return;
    setEditing(null);
    setDeleting(null);
    setWithdrawing(null);
    setModalError(null);
  };

  const changeView = (v: ExpenseView) => {
    setParams(v === 'expenses' ? {} : { view: v }, { replace: true });
    setPage(1);
  };
  const changeMonth = (m: string) => {
    setMonth(m);
    setPage(1);
  };

  const onEdit = useCallback((e: ExpenseDTO) => setEditing(e), []);
  const onDelete = useCallback((e: ExpenseDTO) => setDeleting(e), []);
  const onWithdraw = useCallback((e: ExpenseDTO) => setWithdrawing(e), []);

  const phrase = selectionPhrase('month', month, month);
  const expenses = result?.items ?? [];
  const noun = view === 'withdrawals' ? 'withdrawals' : 'expenses';

  return (
    <>
      <PageHeader
        title="Expense ledger"
        aside={
          <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => setEditing('new')}>
            Add expense
          </Button>
        }
      />

      <Tabs
        label="Expense ledger"
        options={VIEW_OPTIONS}
        value={view}
        onChange={changeView}
        aside={<PeriodPicker granularity="month" from={month} to={month} onChange={changeMonth} label="Month" ranges={false} />}
      />

      {summary && <Summary view={view} summary={summary} phrase={phrase} />}

      <div className={styles.toolbar}>
        <span className={styles.count}>{result && `${plural(result.total, noun.slice(0, -1), noun)}${query ? ' found' : ''}`}</span>
        <TextField
          size="md"
          aria-label={`Search ${noun} by reason`}
          placeholder="Search reason"
          prefix={<SearchIcon size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.search}
        />
      </div>

      {error && <InlineAlert message={error} onDismiss={() => setError(null)} />}

      {!result && loading ? (
        <p className={styles.empty}>Loading {noun}…</p>
      ) : expenses.length === 0 ? (
        <p className={styles.empty}>{query ? `No ${noun} match "${query}"` : `No ${noun} ${phrase}`}</p>
      ) : (
        <ul className={styles.list} aria-busy={loading}>
          {expenses.map((e) => (
            <ExpenseRow key={e.id} expense={e} view={view} onEdit={onEdit} onDelete={onDelete} onWithdraw={onWithdraw} />
          ))}
        </ul>
      )}

      {result && <Pagination page={result.page} pageSize={result.pageSize} total={result.total} onPageChange={setPage} />}

      {editing && (
        <ExpenseFormModal
          key={editing === 'new' ? 'new' : editing.id}
          expense={editing === 'new' ? null : editing}
          busy={busy}
          error={modalError}
          onSave={save}
          onClose={closeModal}
        />
      )}

      <ConfirmModal
        open={!!deleting}
        title="Delete expense"
        message={
          <>
            Are you sure you want to delete <strong>{deleting?.reason}</strong> ({deleting && formatINR(deleting.amount)})?
          </>
        }
        confirmLabel="Delete"
        busyLabel="Deleting…"
        busy={busy}
        error={modalError}
        onConfirm={() => deleting && void run(() => expensesApi.remove(deleting.id))}
        onCancel={closeModal}
      />

      {withdrawing && (
        <WithdrawModal
          key={withdrawing.id}
          expense={withdrawing}
          busy={busy}
          error={modalError}
          onWithdraw={(from) => void run(() => expensesApi.withdraw(withdrawing.id, { from }))}
          onClose={closeModal}
        />
      )}
    </>
  );
}
